import { Link } from "react-router-dom";
import { 
  CheckCircle, 
  ArrowRight, 
  Phone,
  ClipboardCheck,
  MessageSquare,
  Cog,
  HeartHandshake
} from "lucide-react";
import { Button } from "@/components/ui/button";

const ServicesPage = () => {
  const services = [
    {
      icon: ClipboardCheck,
      title: "Évaluation complète et personnalisée",
      description: "Un examen approfondi de votre situation dentaire pour comprendre vos besoins spécifiques et déterminer la meilleure solution pour vous."
    },
    {
      icon: MessageSquare,
      title: "Explication détaillée des options",
      description: "Nous prenons le temps de vous expliquer clairement chaque option, sans jargon technique, pour que vous puissiez prendre une décision éclairée."
    },
    {
      icon: Cog,
      title: "Conception précise et ajustements minutieux",
      description: "Chaque prothèse est conçue sur mesure avec une attention particulière aux détails pour garantir un ajustement parfait et confortable."
    },
    {
      icon: HeartHandshake,
      title: "Suivi et accompagnement",
      description: "Nous restons à vos côtés après l'installation pour nous assurer de votre satisfaction et effectuer les ajustements nécessaires."
    }
  ];

  const prosthesisTypes = [
    {
      title: "Prothèses complètes",
      description: "Les prothèses complètes remplacent l'ensemble des dents d'une mâchoire supérieure ou inférieure. Elles redonnent une fonctionnalité masticatoire complète et un sourire naturel.",
      benefits: ["Restauration complète du sourire", "Amélioration de la mastication", "Support des structures faciales"]
    },
    {
      title: "Prothèses partielles",
      description: "Les prothèses partielles permettent de remplacer une ou plusieurs dents manquantes tout en conservant vos dents naturelles saines.",
      benefits: ["Conservation des dents naturelles", "Solution économique", "Facilité d'adaptation"]
    },
    {
      title: "Prothèses sur implants",
      description: "Les prothèses sur implants offrent une stabilité supérieure en s'ancrant directement aux implants dentaires, pour un confort et une confiance accrus.",
      benefits: ["Stabilité maximale", "Confort supérieur", "Préservation osseuse"]
    }
  ];

  return (
    <div className="bg-[#F5F5F0]" data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-white section-padding" data-testid="services-hero">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="heading-playfair heading-xl text-slate-900 mb-6">
              Nos services en denturologie
            </h1>
            <p className="text-body-lg mb-8">
              Nous offrons une gamme complète de prothèses dentaires: complètes, partielles et sur implants. 
              Chaque solution est personnalisée pour répondre à vos besoins spécifiques.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="services-hero-cta">
                <Button className="btn-primary text-base px-8 py-6">
                  Prendre rendez-vous
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <a 
                href="tel:450-441-3636" 
                className="btn-call text-base"
                data-testid="services-hero-call"
              >
                <Phone className="w-5 h-5" />
                <span>450-441-3636</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Types of Prosthesis */}
      <section className="section-padding" data-testid="services-types">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="heading-playfair heading-lg text-slate-900 mb-4">
              Types de prothèses dentaires
            </h2>
            <p className="text-body-lg max-w-2xl mx-auto">
              Découvrez nos différentes solutions pour retrouver un sourire complet
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {prosthesisTypes.map((type, index) => (
              <div
                key={index}
                className="card p-8 hover:shadow-lg transition-shadow"
                data-testid={`prosthesis-type-${index}`}
              >
                <h3 className="heading-playfair text-xl font-semibold mb-4 text-slate-900">
                  {type.title}
                </h3>
                <p className="text-body mb-6 leading-relaxed">
                  {type.description}
                </p>
                <ul className="space-y-2">
                  {type.benefits.map((benefit, benefitIndex) => (
                    <li key={benefitIndex} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="bg-white section-padding" data-testid="services-offer">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="heading-playfair heading-lg text-slate-900 mb-4">
              Notre approche
            </h2>
            <p className="text-body-lg max-w-2xl mx-auto">
              Une démarche complète et personnalisée pour chaque patient
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="card p-8 hover:shadow-lg transition-shadow"
                data-testid={`service-card-${index}`}
              >
                <div className="icon-circle mb-6 bg-[#F5F5F0]">
                  <service.icon className="w-6 h-6 text-slate-900" />
                </div>
                <h3 className="heading-playfair text-xl font-semibold mb-4 text-slate-900">
                  {service.title}
                </h3>
                <p className="text-body leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding" data-testid="services-benefits">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="heading-playfair heading-lg text-slate-900 mb-6">
                Les avantages de nos prothèses
              </h2>
              <p className="text-body-lg mb-8">
                Une prothèse bien ajustée peut transformer votre qualité de vie 
                en rétablissant vos fonctions essentielles et votre confiance.
              </p>
              <ul className="space-y-4">
                {[
                  "Remplacer une ou plusieurs dents manquantes",
                  "Retrouver une mastication efficace",
                  "Améliorer votre élocution",
                  "Préserver la structure de votre visage",
                  "Regagner confiance en votre sourire"
                ].map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="text-body">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="hero-image aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_sourires-precision/artifacts/udca4jzh_3C048F0B-08D8-49CC-8143-DE73D82FF076.PNG"
                alt="Salle de traitement de la clinique Sourires Saint-Bruno"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Goal */}
      <section className="bg-white section-padding" data-testid="services-goal">
        <div className="container-custom">
          <div className="bg-slate-900 rounded-3xl p-8 md:p-12 lg:p-16 text-center">
            <h2 className="heading-playfair text-2xl md:text-3xl text-white mb-6">
              Notre objectif
            </h2>
            <p className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto mb-8">
              Rétablir votre confort, votre mastication et votre confiance. 
              Chaque prothèse est conçue avec soin pour vous offrir un sourire 
              naturel et fonctionnel.
            </p>
            <Link to="/processus" data-testid="services-process-cta">
              <Button className="btn-call text-base px-8 py-6">
                Découvrir notre processus
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" data-testid="services-cta">
        <div className="container-custom text-center">
          <h2 className="heading-playfair heading-lg text-slate-900 mb-4">
            Prêt à commencer?
          </h2>
          <p className="text-body-lg max-w-2xl mx-auto mb-8">
            Contactez-nous pour une évaluation personnalisée et découvrez 
            comment nous pouvons vous aider à retrouver votre sourire.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" data-testid="services-final-cta">
              <Button className="btn-primary text-base px-8 py-6">
                Demander une consultation
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <a 
              href="tel:450-441-3636" 
              className="btn-secondary text-base px-8 py-6"
              data-testid="services-final-call"
            >
              <Phone className="w-5 h-5" />
              <span>Appelez-nous</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
